import React, { useEffect, useState, useRef } from 'react';
import type { FontFamily } from './Itinerary';

interface DressCodeProps {
  titleFont?: FontFamily;
  headingFont?: FontFamily;
  textFont?: FontFamily;
}

const DressCode: React.FC<DressCodeProps> = ({
  titleFont = 'parisienne',
  headingFont = 'baskerville',
  textFont = 'baskerville'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="dress-code"
      ref={sectionRef}
      className="relative py-20 px-4 overflow-hidden"
    >
      <div className="max-w-4xl mx-auto">
        {/* Título y descripción */}
        <div 
          className={`text-center mb-16 transition-all duration-1000 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h3 className={`text-4xl md:text-5xl font-${titleFont} text-forest mb-6`}>
            Código de vestimenta
          </h3>
          <p className={`text-forest/80 mb-6 text-lg font-${textFont}`}>Formal</p>
          <div className="w-24 h-0.5 bg-sage/30 mx-auto"></div>
        </div>
        
        {/* Contenedor para Caballeros y Damas como cuadros separados */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Caballeros - Cuadro independiente */}
          <div 
            className={`bg-cream/70 backdrop-blur-sm rounded-lg shadow-md p-8 transition-all duration-1000 transform hover:shadow-lg ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="flex flex-col items-center">
              <h4 className={`text-2xl font-${headingFont} text-forest mb-6`}>
                Caballeros
              </h4>
              <div className="w-48 h-48 mb-6 flex items-center justify-center">
                <img 
                  src="/lovable-uploads/caballeros.png" 
                  alt="Traje y corbata" 
                  className="max-w-full max-h-full"
                />
              </div>
              <p className={`text-forest/80 text-lg font-${textFont}`}>Traje y Corbata</p>
            </div>
          </div>
          
          {/* Damas - Cuadro independiente */}
          <div 
            className={`bg-cream/70 backdrop-blur-sm rounded-lg shadow-md p-8 transition-all duration-1000 transform hover:shadow-lg ${
              isVisible ? 'opacity-100 translate-y-0 delay-200' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="flex flex-col items-center">
              <h4 className={`text-2xl font-${headingFont} text-forest mb-6`}>
                Damas
              </h4>
              <div className="w-48 h-48 mb-6 flex items-center justify-center">
                <img 
                  src="/lovable-uploads/damas.png" 
                  alt="Vestido largo" 
                  className="max-w-full max-h-full"
                />
              </div>
              <p className={`text-forest/80 text-lg font-${textFont}`}>Vestido Largo</p>
            </div>
          </div>
        </div>
        
        {/* Botón de Ver Ideas */}
        <div className={`mt-16 flex justify-center transition-all duration-1000 transform ${
          isVisible ? 'opacity-100 translate-y-0 delay-300' : 'opacity-0 translate-y-10'
        }`}>
          <a 
            href="https://pin.it/6yd5AjRph" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block px-8 py-3 border border-forest text-forest font-${textFont} hover:bg-forest hover:text-cream transition-colors duration-300 rounded-sm"
          >
            VER IDEAS
          </a>
        </div>
      </div>
    </section>
  );
};

export default DressCode;